/**
 * TypewriterLabel — terminal typewriter effect on hover.
 *
 * - Fixed width: invisible full-text span holds the width; animated text is
 *   absolutely overlaid, so the button never resizes during typing.
 * - Cursor: blinking _ (via ov-cursor CSS animation) throughout the hover,
 *   not just while typing.
 * - Speed: 45ms per character.
 * - On hover-out: text snaps back to full, cursor gone.
 * - aria-label preserves full text for screen readers.
 */
import { useState, useEffect, useRef } from 'react';

const SPEED = 45; // ms per character

export default function TypewriterLabel({ children }) {
  const text    = String(children);
  const spanRef = useRef(null);
  const timerRef = useRef(null);

  const [displayed, setDisplayed] = useState(text);
  const [hovering,  setHovering]  = useState(false);
  const [typing,    setTyping]    = useState(false);

  useEffect(() => {
    const el = spanRef.current?.closest('a, button');
    if (!el) return;

    function start() {
      clearTimeout(timerRef.current);
      setHovering(true);
      setTyping(true);
      setDisplayed('');
      let i = 0;
      const type = () => {
        i++;
        setDisplayed(text.slice(0, i));
        if (i < text.length) {
          timerRef.current = setTimeout(type, SPEED);
        } else {
          setTyping(false); // cursor switches to blink animation
        }
      };
      timerRef.current = setTimeout(type, 0);
    }

    function stop() {
      clearTimeout(timerRef.current);
      setDisplayed(text);
      setHovering(false);
      setTyping(false);
    }

    el.addEventListener('mouseenter', start);
    el.addEventListener('mouseleave', stop);

    return () => {
      clearTimeout(timerRef.current);
      el.removeEventListener('mouseenter', start);
      el.removeEventListener('mouseleave', stop);
    };
  }, [text]);

  return (
    <span
      ref={spanRef}
      aria-label={text}
      style={{ position: 'relative', display: 'inline-block' }}
    >
      {/* Invisible full text — holds the button width constant */}
      <span style={{ visibility: 'hidden', userSelect: 'none' }} aria-hidden="true">
        {text}
      </span>
      {/* Visible animated text — absolutely overlaid */}
      <span style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', whiteSpace: 'nowrap' }}>
        {displayed}
        {hovering && (
          <span className={typing ? '' : 'ov-cursor'}>_</span>
        )}
      </span>
    </span>
  );
}
